/**
 * TrendingProductsCard - Top trending products by region (BR/US)
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logRequest } from '@/hooks/useRequestLogger';
import { Package, ArrowRight, Lock, ExternalLink, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useRole } from '@/contexts/RoleContext';
import { usePVRedirect } from '@/hooks/usePVRedirect';
import { OptimizedImage } from '@/components/OptimizedImage';
import { formatGmvDisplay } from '@/lib/formatCurrency';

interface TrendingProduct {
  id: string;
  name: string;
  image_url: string | null;
  monthly_gmv: number | null;
  monthly_sales: number | null;
  influencers_count: number | null;
  rank: number;
  region: string;
}

async function fetchTrendingByRegion(region: string): Promise<TrendingProduct[]> {
  const { data, error } = await supabase
    .from('trending_products_cache')
    .select('id, name, image_url, monthly_gmv, monthly_sales, influencers_count, rank, region')
    .eq('region', region)
    .order('rank', { ascending: true })
    .limit(3);

  if (error) return [];
  logRequest('marketplace_products');
  return ((data ?? []) as unknown) as TrendingProduct[];
}


function TrendingProductsCardInner() {
  const { effectiveRole } = useRole();
  const { redirectToPV } = usePVRedirect();
  const isVisitor = effectiveRole === 'visitor';
  const [activeRegion, setActiveRegion] = useState<'br' | 'us'>('br');

  const { data: brProducts = [], isLoading: brLoading } = useQuery({
    queryKey: ['trending-home', 'br'],
    queryFn: () => fetchTrendingByRegion('br'),
    staleTime: 5 * 60 * 1000,
  });

  const { data: usProducts = [], isLoading: usLoading } = useQuery({
    queryKey: ['trending-home', 'us'],
    queryFn: () => fetchTrendingByRegion('us'),
    staleTime: 5 * 60 * 1000,
  });

  const products = activeRegion === 'br' ? brProducts : usProducts;
  const isLoading = activeRegion === 'br' ? brLoading : usLoading;
  const totalCount = brProducts.length + usProducts.length;

  if (!brLoading && !usLoading && totalCount === 0) return null;

  return (
    <div className="forja-card rounded-xl p-3 md:p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20">
            <Package className="w-3 h-3 md:w-3.5 md:h-3.5 text-primary" />
          </div>
          <h2 className="font-display text-sm md:text-base font-bold text-foreground">
            Produtos que dominam o TikTok Shop
          </h2>
        </div>
      </div>

      {/* Region tabs */}
      <div className="flex gap-1.5 mb-3">
        <button
          onClick={() => setActiveRegion('br')}
          className={cn(
            "flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
            activeRegion === 'br'
              ? "bg-primary/10 text-primary border border-primary/20"
              : "bg-secondary/50 text-muted-foreground border border-border/30 hover:bg-secondary/80"
          )}
        >
          🇧🇷 Brasil
        </button>
        <button
          onClick={() => setActiveRegion('us')}
          className={cn(
            "flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
            activeRegion === 'us'
              ? "bg-primary/10 text-primary border border-primary/20"
              : "bg-secondary/50 text-muted-foreground border border-border/30 hover:bg-secondary/80"
          )}
        >
          🇺🇸 Estados Unidos
        </button>
      </div>

      {/* Product list */}
      {isLoading ? (
        <div className="space-y-2 mb-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      ) : products.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {products.map((product, idx) => (
            <div
              key={product.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-secondary/40 border border-border/30"
            >
              <span className="text-[10px] font-bold text-muted-foreground w-4 text-center flex-shrink-0">
                {idx + 1}
              </span>
              {product.image_url && (
                <div className={cn(
                  "w-8 h-8 rounded-md overflow-hidden flex-shrink-0 border border-border/30",
                  isVisitor && "blur-[4px]"
                )}>
                  <OptimizedImage src={product.image_url} alt="" className="w-full h-full" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-xs font-medium text-foreground truncate",
                  isVisitor && "blur-[3px] select-none"
                )}>
                  {product.name}
                </p>
                <div className="flex items-center gap-2">
                  {product.monthly_gmv && (
                    <span className={cn(
                      "text-[9px] text-emerald-400 font-semibold",
                      isVisitor && "blur-[3px]"
                    )}>
                      GMV {formatGmvDisplay(product.monthly_gmv)}
                    </span>
                  )}
                  {product.influencers_count && (
                    <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                      <Users className="w-2.5 h-2.5" />
                      {product.influencers_count}
                    </span>
                  )}
                </div>
              </div>
              {isVisitor && <Lock className="w-3 h-3 text-muted-foreground/40 flex-shrink-0" />}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-6 rounded-lg bg-secondary/20 mb-3">
          <p className="text-[10px] text-muted-foreground">Nenhum produto detectado nesta região.</p>
        </div>
      )}

      {/* CTA */}
      <div className="mt-auto">
      {isVisitor ? (
        <Button
          size="sm"
          variant="outline"
          className="w-full gap-1.5 text-xs"
          onClick={() => redirectToPV('trending-card')}
        >
          Quero ser membro
          <ExternalLink className="w-3.5 h-3.5" />
        </Button>
      ) : (
        <Link to="/arsenal" className="block">
          <Button size="sm" variant="ghost" className="w-full gap-1.5 text-xs">
            Ver ranking completo
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </Link>
      )}
      </div>
    </div>
  );
}

const TrendingProductsCard = React.memo(TrendingProductsCardInner);
export default TrendingProductsCard;
